import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SlideOver, SlideOverFooter } from '@/components/Shared/SlideOver';
import { FormField } from '@/components/Shared/FormField';
import { TextArea } from '@/components/Shared/TextArea';
import { useOvertimeStore } from '@/store/useOvertimeStore';

interface OvertimeSetupSlideOverProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string;
  employeeName: string;
  /** Vorbelegung aus dem aktuellen Konto, damit ein erneutes Öffnen nichts verliert. */
  currentStartDate?: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

/**
 * Richtet das Überstundenkonto beim Umstieg ein: ab wann gezählt wird und was der Mitarbeiter
 * aus dem Altsystem mitbringt. Beides gehört zusammen — ein Stichtag ohne Saldo verliert die
 * Historie, ein Saldo ohne Stichtag ertrinkt in den Monaten davor (ADR-0015).
 */
export function OvertimeSetupSlideOver({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  currentStartDate,
}: OvertimeSetupSlideOverProps) {
  const { t } = useTranslation(['timeclock', 'common']);
  const { setUpAccount, saving } = useOvertimeStore();

  const [startDate, setStartDate] = useState('');
  const [opening, setOpening] = useState('0');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStartDate(currentStartDate ?? todayIso());
      setOpening('0');
      setNote('');
    }
  }, [isOpen, currentStartDate]);

  const parsedOpening = Number(opening.replace(',', '.'));
  const openingIsValid = opening !== '' && Number.isFinite(parsedOpening);
  // Eine Notiz ist nur Pflicht, wenn tatsächlich ein Saldo gebucht wird — bei 0 gibt es
  // nichts zu begründen, und der Server schreibt dann auch keine Zeile.
  const noteRequired = openingIsValid && parsedOpening !== 0;
  const isValid = startDate !== '' && openingIsValid && (!noteRequired || note.trim() !== '');

  const handleSave = async () => {
    if (!isValid) return;
    try {
      await setUpAccount(employeeId, startDate, parsedOpening, note.trim() || undefined);
      onClose();
    } catch {
      // Fehlermeldung kommt aus dem Store.
    }
  };

  return (
    <SlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('timeclock:overtime.setupDialog.title')}
      size="md"
      footer={
        <SlideOverFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmText={t('timeclock:overtime.setupDialog.save')}
          cancelText={t('common:actions.cancel')}
          loading={saving}
          confirmDisabled={!isValid}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-[13px] text-slate-500">
          {t('timeclock:overtime.setupDialog.subtitle', { employee: employeeName })}
        </p>

        <FormField
          label={t('timeclock:overtime.setupDialog.startDateLabel')}
          required
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="tabular-nums"
          helperText={t('timeclock:overtime.setupDialog.startDateHint')}
        />

        <FormField
          label={t('timeclock:overtime.setupDialog.openingLabel')}
          required
          type="number"
          step={0.25}
          value={opening}
          onChange={(e) => setOpening(e.target.value)}
          className="tabular-nums"
          helperText={t('timeclock:overtime.setupDialog.openingHint')}
        />

        <TextArea
          label={t('timeclock:overtime.setupDialog.noteLabel')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          required={noteRequired}
          placeholder={t('timeclock:overtime.setupDialog.notePlaceholder')}
        />
      </div>
    </SlideOver>
  );
}
